import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import './DataPage.css';
import './TesteIA.css';

export default function TesteIA() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const [form, setForm] = useState({
    descricao: '',
    num_conversas: 5,
    modelos: ['deepseek/deepseek-v4-flash'],
  });
  const [creating, setCreating] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/ai-testes/runs?limit=50`);
      setRuns(data);
    } catch (err) {
      console.error('Erro ao listar runs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const [catalogoErro, setCatalogoErro] = useState(null);
  const carregarCatalogo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/ai-testes/modelos-catalogo`);
      setCatalogo(data);
      setCatalogoErro(null);
    } catch (err) {
      console.error('Erro ao carregar catálogo de modelos:', err);
      setCatalogoErro(err.response?.data?.error || err.message || 'Erro desconhecido');
    }
  }, []);

  useEffect(() => {
    carregar();
    carregarCatalogo();
    const t = setInterval(carregar, 5000);
    return () => clearInterval(t);
  }, [carregar, carregarCatalogo]);

  const toggleModelo = (id) => {
    setForm(f => ({
      ...f,
      modelos: f.modelos.includes(id) ? f.modelos.filter(m => m !== id) : [...f.modelos, id],
    }));
  };

  const submeter = async () => {
    if (form.modelos.length === 0) { alert('Selecione ao menos 1 modelo'); return; }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/ai-testes/runs`, form);
      setModalOpen(false);
      setForm({ descricao: '', num_conversas: 5, modelos: ['deepseek/deepseek-v4-flash'] });
      await carregar();
      navigate(`/teste-ia/${data.id}`);
    } catch (err) {
      alert('Erro ao criar teste: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreating(false);
    }
  };

  const remover = async (id) => {
    if (!window.confirm(`Remover teste #${id}? Os resultados serão perdidos.`)) return;
    try {
      await axios.delete(`${API_URL}/api/ai-testes/runs/${id}`);
      carregar();
    } catch (err) {
      alert('Erro ao remover: ' + (err.response?.data?.error || err.message));
    }
  };

  const statusBadge = (status) => {
    const colors = {
      'em_execucao': '#1976d2',
      'concluido': '#2e7d32',
      'erro': '#c62828',
    };
    const labels = { em_execucao: 'Em execução', concluido: 'Concluído', erro: 'Erro' };
    return (
      <span style={{
        background: colors[status] || '#666',
        color: '#fff', padding: '2px 10px', borderRadius: 12,
        fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
      }}>{labels[status] || status}</span>
    );
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('pt-BR') : '-';
  const fmtTokens = (n) => n ? (n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n)) : '0';
  const fmtUsd = (v) => v != null ? `$${Number(v).toFixed(4)}` : '-';

  return (
    <div className="data-page">
      <div className="page-header">
        <h1>🧪 Teste de IA</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" onClick={() => navigate('/teste-ia/analises')}>📑 Análises consolidadas</button>
          <button className="btn-primary" onClick={() => setModalOpen(true)}>+ Novo teste</button>
        </div>
      </div>

      <p style={{ color: '#666', marginBottom: 16 }}>
        Importa N conversas reais do Chatwoot, identifica o último ponto onde uma
        atendente humana respondeu, e roda a mesma conversa contra os modelos selecionados
        — pra comparar a resposta da IA com a resposta humana real.
      </p>

      {loading ? <p>Carregando...</p> : (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 60 }}>#</th>
              <th>Descrição</th>
              <th style={{ width: 100 }}>Status</th>
              <th style={{ width: 130 }}>Progresso</th>
              <th style={{ width: 100 }}>Casos</th>
              <th>Modelos</th>
              <th style={{ width: 100 }}>Tokens</th>
              <th style={{ width: 100 }}>Custo</th>
              <th style={{ width: 150 }}>Criado em</th>
              <th style={{ width: 130 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 30, color: '#888' }}>
                Nenhum teste rodado. Clique em "+ Novo teste" pra começar.
              </td></tr>
            ) : runs.map(r => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td title={r.descricao}>{r.descricao || <em style={{ color: '#999' }}>(sem descrição)</em>}</td>
                <td>{statusBadge(r.status)}</td>
                <td>
                  {r.progresso_total > 0 ? (
                    <div style={{ background: '#eee', borderRadius: 8, overflow: 'hidden', height: 18, position: 'relative' }}>
                      <div style={{
                        background: r.status === 'erro' ? '#c62828' : '#1976d2',
                        width: `${(r.progresso_atual / r.progresso_total) * 100}%`,
                        height: '100%', transition: 'width 0.5s',
                      }} />
                      <span style={{
                        position: 'absolute', inset: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 600,
                      }}>{r.progresso_atual}/{r.progresso_total}</span>
                    </div>
                  ) : '-'}
                </td>
                <td>{r.cases_count} {r.vereditos_count > 0 && <small style={{ color: '#2e7d32' }}>· {r.vereditos_count} avaliados</small>}</td>
                <td title={r.modelos.join(', ')}>
                  {r.modelos.length === 1 ? r.modelos[0].split('/').pop() : `${r.modelos.length} modelos`}
                </td>
                <td>{fmtTokens(r.total_tokens)}</td>
                <td>{fmtUsd(r.custo_total_usd)}</td>
                <td><small>{formatDate(r.created_at)}</small></td>
                <td>
                  <button className="btn-link" onClick={() => navigate(`/teste-ia/${r.id}`)}>Ver</button>
                  {' · '}
                  <button className="btn-link" style={{ color: '#c62828' }} onClick={() => remover(r.id)}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modalOpen && (
        <div className="modal-backdrop" onClick={() => setModalOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <h2>Novo teste de IA</h2>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Descrição (opcional)</label>
              <input
                type="text" value={form.descricao}
                onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                placeholder="ex: Comparativo após ajuste de temperatura"
                style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
                Quantidade de conversas reais: <strong>{form.num_conversas}</strong>
              </label>
              <input
                type="range" min={1} max={15} value={form.num_conversas}
                onChange={e => setForm(f => ({ ...f, num_conversas: parseInt(e.target.value) }))}
                style={{ width: '100%' }}
              />
              <small style={{ color: '#666' }}>Cada conversa importada do Chatwoot vai rodar contra TODOS os modelos selecionados.</small>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>
                Modelos OpenRouter ({form.modelos.length} selecionados)
              </label>
              <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4, padding: 8 }}>
                {catalogoErro && (
                  <div style={{ color: '#c62828', padding: 12, fontSize: 13 }}>
                    ⚠️ Não foi possível carregar o catálogo: {catalogoErro}<br/>
                    <small>(precisa estar logado como admin)</small>
                  </div>
                )}
                {!catalogoErro && catalogo.length === 0 && (
                  <div style={{ color: '#888', padding: 12, fontSize: 13 }}>Carregando modelos...</div>
                )}
                {catalogo.map(m => (
                  <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}>
                    <input type="checkbox" checked={form.modelos.includes(m.id)} onChange={() => toggleModelo(m.id)} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{m.label}</div>
                      <small style={{ color: '#888' }}>{m.id} · in ${m.in_M}/M · out ${m.out_M}/M</small>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ background: '#fff8e1', padding: 10, borderRadius: 4, fontSize: 13, marginBottom: 16 }}>
              ⚠️ Custo estimado por run = <strong>{form.num_conversas} conv × {form.modelos.length} modelos = {form.num_conversas * form.modelos.length} chamadas</strong>.
              Cada chamada usa ~2-4k tokens. Veja preço de cada modelo acima.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setModalOpen(false)} disabled={creating}>Cancelar</button>
              <button className="btn-primary" onClick={submeter} disabled={creating || form.modelos.length === 0}>
                {creating ? 'Criando...' : 'Iniciar teste'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
